class CasaGomezVideoBanner extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._slides = Array.from(this.querySelectorAll('[data-cg-slide]'));
    this._players = new Map();
    this._readyPlayers = new Set();
    this._manualRequests = new Set();
    this._listeners = [];
    this._index = 0;
    if (!this._slides.length) return;

    this._slides.forEach((slide, index) => {
      const button = slide.querySelector('[data-cg-play]');
      if (button) this._listen(button, 'click', () => {
        clearTimeout(this._timer);
        this._play(slide, index, true);
      });
      const video = slide.querySelector('video');
      if (!video) return;
      this._listen(video, 'playing', () => {
        if (this._index === index) this._setPlaying(slide, true);
      });
      this._listen(video, 'pause', () => {
        this._setPlaying(slide, false, this._index === index);
      });
      this._listen(video, 'error', () => this._setPlaying(slide, false, true));
      this._listen(video, 'ended', () => {
        if (!this._connected || this._index !== index) return;
        if (this._slides.length === 1) {
          video.currentTime = 0;
          this._playNative(slide, video);
        } else {
          this._activate((index + 1) % this._slides.length);
        }
      });
    });
    this._activate(0);
  }

  _listen(target, event, callback) {
    target.addEventListener(event, callback);
    this._listeners.push([target, event, callback]);
  }

  _setPlaying(slide, playing, showButton = false) {
    slide.classList.toggle(this.dataset.playingClass, playing);
    slide.toggleAttribute('data-cg-playable', !playing && showButton);
  }

  _activate(index) {
    clearTimeout(this._timer);
    clearTimeout(this._apiTimer);
    const oldSlide = this._slides[this._index];
    if (oldSlide && this._index !== index) {
      oldSlide.querySelector('video')?.pause();
      this._players.get(this._index)?.pauseVideo();
      this._setPlaying(oldSlide, false);
    }
    this._index = index;
    this._slides.forEach((slide, position) => {
      slide.classList.toggle(this.dataset.activeClass, position === index);
    });
    const slide = this._slides[index];
    const hasVideo = !!slide.querySelector('video, [data-youtube-template]');
    if (hasVideo && slide.dataset.autoplay === 'true') {
      this._play(slide, index);
    } else if (this._slides.length > 1) {
      const interval = Math.max(1, Number(this.dataset.interval) || 5000);
      this._timer = setTimeout(() => {
        if (this._connected) this._activate((index + 1) % this._slides.length);
      }, interval);
    }
  }

  _play(slide, index, manual = false) {
    const video = slide.querySelector('video');
    if (video) {
      this._playNative(slide, video);
      return;
    }
    if (!slide.querySelector('[data-youtube-template]')) return;
    this._playYouTube(slide, index, manual);
  }

  _playNative(slide, video) {
    if (!video.hasAttribute('src')) {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const source = mobile ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      if (!source) return;
      video.src = source;
    }
    try {
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {
        if (this._connected && slide === this._slides[this._index]) {
          this._setPlaying(slide, false, true);
        }
      });
    } catch {
      this._setPlaying(slide, false, true);
    }
  }

  _playYouTube(slide, index, manual) {
    if (manual) this._manualRequests.add(index);
    const existing = this._players.get(index);
    if (existing) {
      if (this._readyPlayers.has(index)) existing.playVideo();
      return;
    }
    if (!window.YT?.loaded) {
      clearTimeout(this._apiTimer);
      this._apiTimer = setTimeout(() => {
        if (this._connected && this._index === index) {
          this._playYouTube(slide, index, manual);
        }
      }, 300);
      return;
    }
    const template = slide.querySelector('[data-youtube-template]');
    const target = template.content.firstElementChild.cloneNode(true);
    const id = target.dataset.videoId;
    slide.querySelector('[data-cg-video-wrapper]').append(target);
    const player = new window.YT.Player(target, {
      height: '100%',
      width: '100%',
      videoId: id,
      playerVars: { mute: 1, playsinline: 1, controls: 0 },
      events: {
        onReady: event => {
          this._readyPlayers.add(index);
          if (!this._connected || this._index !== index) return;
          if (this._manualRequests.has(index) || slide.dataset.autoplay === 'true') event.target.playVideo();
        },
        onStateChange: event => {
          if (!this._connected || this._index !== index) return;
          if (event.data === window.YT.PlayerState.PLAYING) {
            this._setPlaying(slide, true);
          } else if (event.data === window.YT.PlayerState.PAUSED) {
            this._setPlaying(slide, false, true);
          } else if (event.data === window.YT.PlayerState.ENDED) {
            if (this._slides.length === 1) event.target.playVideo();
            else this._activate((index + 1) % this._slides.length);
          }
        },
        onError: () => this._setPlaying(slide, false, true)
      }
    });
    this._players.set(index, player);
  }

  disconnectedCallback() {
    this._connected = false;
    clearTimeout(this._timer);
    clearTimeout(this._apiTimer);
    this._listeners?.forEach(([target, event, callback]) => {
      target.removeEventListener(event, callback);
    });
    this.querySelectorAll('video').forEach(video => video.pause());
    this._players?.forEach(player => player.destroy());
  }
}

if (!customElements.get('cg-video-banner')) {
  customElements.define('cg-video-banner', CasaGomezVideoBanner);
}
